import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// EmptyState — a centered icon-in-circle + title + message + optional action button, used for
// the empty / no-results states inside a TableCard (and reusable anywhere a calm empty block
// is needed).
//
// `icon` is a *resolved* FontAwesome icon object. Callers build it with the icon() macro at
// their own call site (the macro only accepts string literals), e.g.
//   <EmptyState icon={icon({ name: "wallet", style: "light" })} ... />
export default function EmptyState({ icon, title, message, action }) {
	return (
		<div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
			{icon && (
				// Fixed px sizing: theme width/height scales are overridden, so w-14/h-14 are no-ops.
				<div className="mb-2 flex h-[56px] w-[56px] items-center justify-center rounded-full bg-surface-2 text-text-3">
					<FontAwesomeIcon icon={icon} className="text-2xl" />
				</div>
			)}
			<p className="text-base font-semibold text-text">{title}</p>
			{message && <p className="max-w-sm text-sm text-text-2">{message}</p>}
			{action && (
				<button
					type="button"
					onClick={action.onClick}
					className={`mt-2 inline-flex items-center gap-1.5 rounded-ctrl px-3 py-1.5 text-[13px] font-medium transition-colors ${
						action.variant === "secondary"
							? "border border-border bg-card text-text hover:bg-surface-2"
							: "bg-primary text-white hover:bg-primary-hover"
					}`}
				>
					{action.icon && <FontAwesomeIcon icon={action.icon} className="text-[13px]" />}
					{action.label}
				</button>
			)}
		</div>
	);
}

EmptyState.propTypes = {
	icon: PropTypes.object,
	title: PropTypes.node.isRequired,
	message: PropTypes.node,
	action: PropTypes.shape({
		label: PropTypes.node.isRequired,
		onClick: PropTypes.func.isRequired,
		icon: PropTypes.object,
		variant: PropTypes.oneOf(["primary", "secondary"]),
	}),
};
